import React, { useState, useCallback, useEffect } from "react";
import { Alert, Button, Input, Select } from "antd";
import {
  CreditCardOutlined,
  CalendarOutlined,
  LockOutlined,
  UserOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Cards from "react-credit-cards-2";
import "react-credit-cards-2/dist/es/styles-compiled.css";
import {
  StyledModal,
  ModalBody,
  CardPreviewPanel,
  PanelOverlay,
  SecurityBadge,
  FormPanel,
  CloseButton,
  PanelHeader,
  AmountDisplay,
  StyledInputWrapper,
  CardFormFields,
  CardFormRow,
  CardFormLabel,
  FieldError,
  AlertsContainer,
  ActionButtons,
} from "./credit-card-form.styled";
import { FormatterUtils } from "@nubeteck/utils";
import { IPowerTranzPlan } from "../../../Interfaces/options";

type Focused = "name" | "number" | "expiry" | "cvc";

const DIACRITICS_REGEX = new RegExp("[\\u0300-\\u036f]", "g");

const normalizeLabel = (text: string): string =>
  text.normalize("NFD").replace(DIACRITICS_REGEX, "").toLowerCase().trim();

interface CreditCardFormProps {
  open: boolean;
  onSubmit: (cardData: {
    cardPan: string;
    cardCvv: string;
    cardExpiration: string;
    cardholderName: string;
    planPago: string;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
  hasOverdueBalance: boolean;
  totalAmount: number;
  currencySymbol: string;
  cardholderName?: string;
  paymentPlans: IPowerTranzPlan[];
  isLoadingPaymentPlans?: boolean;
}

function CreditCardForm({
  open,
  onSubmit,
  onCancel,
  isLoading,
  hasOverdueBalance,
  totalAmount,
  currencySymbol,
  cardholderName = "",
  paymentPlans,
  isLoadingPaymentPlans = false,
}: CreditCardFormProps) {
  const [number, setNumber] = useState("");
  const [name, setName] = useState(cardholderName);
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [planPago, setPlanPago] = useState<string | undefined>(undefined);
  const [focused, setFocused] = useState<Focused | undefined>(undefined);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (paymentPlans.length === 1) {
      setPlanPago(paymentPlans[0].key);
    } else if (paymentPlans.length > 1 && !paymentPlans.some((p) => p.key === planPago)) {
      const pagoUnico = paymentPlans.find((p) => normalizeLabel(p.label) === "pago unico");
      setPlanPago(pagoUnico?.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPlans]);

  const resetForm = useCallback(() => {
    setNumber("");
    setName(cardholderName);
    setExpiry("");
    setCvc("");
    setFocused(undefined);
    setErrors({});
  }, [cardholderName]);

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(" ") : "";
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 16);
    setNumber(digits);
    if (errors.number) setErrors((prev) => ({ ...prev, number: "" }));
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 4) value = value.slice(0, 4);
    if (value.length >= 3) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }
    setExpiry(value);
    if (errors.expiry) setErrors((prev) => ({ ...prev, expiry: "" }));
  };

  const handleCvcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    setCvc(digits);
    if (errors.cvc) setErrors((prev) => ({ ...prev, cvc: "" }));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (number.length < 13) newErrors.number = "Número de tarjeta inválido";
    if (!expiry || expiry.length < 4) {
      newErrors.expiry = "Fecha requerida (MM/AA)";
    } else {
      const expiryDigits = expiry.replace("/", "");
      const mm = parseInt(expiryDigits.slice(0, 2), 10);
      const yy = parseInt(expiryDigits.slice(2, 4), 10);
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear() % 100;
      if (yy < currentYear || (yy === currentYear && mm < currentMonth)) {
        newErrors.expiry = "La tarjeta está vencida";
      }
    }
    if (!cvc || cvc.length < 3) newErrors.cvc = "CVV requerido";
    if (!name.trim()) newErrors.name = "Nombre del titular requerido";
    if (paymentPlans.length > 1 && !planPago) newErrors.planPago = "Seleccione un plan de pago";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const expiryDigits = expiry.replace("/", "");
    const mm = expiryDigits.slice(0, 2);
    const yy = expiryDigits.slice(2, 4);
    const cardExpiration = yy + mm;

    onSubmit({
      cardPan: number,
      cardCvv: cvc,
      cardExpiration,
      cardholderName: name,
      planPago: planPago!,
    });
  };

  const formattedAmount = FormatterUtils.number(totalAmount.toFixed(2));

  return (
    <StyledModal
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      closable={false}
      title={null}
      footer={null}
      width={880}
      centered
    >
      <ModalBody>
        <CardPreviewPanel>
          <PanelOverlay />
          <Cards
            number={formatCardNumber(number)}
            name={name || " "}
            expiry={expiry.replace("/", "")}
            cvc={cvc}
            focused={focused}
          />
          <SecurityBadge>
            <LockOutlined />
            Pago seguro y encriptado
          </SecurityBadge>
        </CardPreviewPanel>

        <FormPanel>
          {!isLoading && (
            <CloseButton onClick={handleCancel} type="button">
              <CloseOutlined />
            </CloseButton>
          )}

          <PanelHeader>
            <h3>Datos de la tarjeta</h3>
            <p>Ingrese la información de su tarjeta de crédito o débito</p>
          </PanelHeader>

          <AmountDisplay>
            <span className="label">Total a pagar</span>
            <span className="amount">
              {currencySymbol}
              {formattedAmount}
            </span>
          </AmountDisplay>

          <StyledInputWrapper>
            <CardFormFields>
              <div>
                <CardFormLabel>Número de tarjeta</CardFormLabel>
                <Input
                  size="large"
                  prefix={<CreditCardOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="4012 0000 0002 0006"
                  value={formatCardNumber(number)}
                  onChange={handleNumberChange}
                  onFocus={() => setFocused("number")}
                  maxLength={19}
                  status={errors.number ? "error" : undefined}
                />
                {errors.number && <FieldError>{errors.number}</FieldError>}
              </div>

              <CardFormRow>
                <div>
                  <CardFormLabel>Expiración</CardFormLabel>
                  <Input
                    size="large"
                    prefix={<CalendarOutlined style={{ color: "#bfbfbf" }} />}
                    placeholder="MM/AA"
                    value={expiry}
                    onChange={handleExpiryChange}
                    onFocus={() => setFocused("expiry")}
                    maxLength={5}
                    status={errors.expiry ? "error" : undefined}
                  />
                  {errors.expiry && <FieldError>{errors.expiry}</FieldError>}
                </div>
                <div>
                  <CardFormLabel>CVV</CardFormLabel>
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                    placeholder="123"
                    value={cvc}
                    onChange={handleCvcChange}
                    onFocus={() => setFocused("cvc")}
                    onBlur={() => setFocused(undefined)}
                    maxLength={4}
                    status={errors.cvc ? "error" : undefined}
                    visibilityToggle={false}
                  />
                  {errors.cvc && <FieldError>{errors.cvc}</FieldError>}
                </div>
              </CardFormRow>

              {paymentPlans.length > 1 && (
                <div>
                  <CardFormLabel>Plan de pago</CardFormLabel>
                  <Select
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="Seleccione un plan de pago"
                    value={planPago}
                    loading={isLoadingPaymentPlans}
                    onChange={(value) => {
                      setPlanPago(value);
                      if (errors.planPago) setErrors((prev) => ({ ...prev, planPago: "" }));
                    }}
                    options={paymentPlans.map((p) => ({
                      label: p.label,
                      value: p.key,
                    }))}
                    status={errors.planPago ? "error" : undefined}
                  />
                  {errors.planPago && <FieldError>{errors.planPago}</FieldError>}
                </div>
              )}
              {paymentPlans.length === 1 && (
                <AmountDisplay>
                  <span className="label">Plan de pago</span>
                  <span className="amount">{paymentPlans[0].label}</span>
                </AmountDisplay>
              )}
              {paymentPlans.length === 0 && (
                <Alert
                  type="error"
                  showIcon
                  message="No hay planes de pago disponibles en este momento. Intente más tarde."
                />
              )}

              <div>
                <CardFormLabel>Nombre del titular</CardFormLabel>
                <Input
                  size="large"
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="Nombre como aparece en la tarjeta"
                  value={name}
                  onChange={handleNameChange}
                  onFocus={() => setFocused("name")}
                  status={errors.name ? "error" : undefined}
                />
                {errors.name && <FieldError>{errors.name}</FieldError>}
              </div>
            </CardFormFields>
          </StyledInputWrapper>

          <AlertsContainer>
            <Alert
              type="info"
              showIcon
              message="Una vez procesado el pago recibirá un correo con los detalles de su recibo."
            />
            {hasOverdueBalance && (
              <Alert
                type="warning"
                showIcon
                message="Si tiene alguna duda respecto a su saldo vencido, comuníquese con el departamento financiero."
              />
            )}
          </AlertsContainer>

          <ActionButtons>
            <Button className="cancel-btn" size="large" onClick={handleCancel} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              className="pay-btn"
              type="primary"
              size="large"
              loading={isLoading}
              disabled={paymentPlans.length === 0}
              onClick={handleSubmit}
            >
              Pagar {currencySymbol}
              {formattedAmount}
            </Button>
          </ActionButtons>
        </FormPanel>
      </ModalBody>
    </StyledModal>
  );
}

export default CreditCardForm;
